import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Image as ImageIcon,
  Group as GroupIcon,
  Folder as FolderIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { BASE_URL } from '../constants/global';
import { groupApi, collectionApi, adminApi } from '../utils/api';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface UserDetail {
  id: number;
  name: string;
  email: string;
  givenName: string;
  affiliation: string;
  subID: string;
  isSuperAdmin: boolean;
}

interface UserStats {
  imageCount: number;
  groupCount: number;
  collectionCount: number;
}

const AdminUserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeDryRun, setPurgeDryRun] = useState(true);
  const [purgeRunning, setPurgeRunning] = useState(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [purgeReport, setPurgeReport] = useState<any | null>(null);

  const { data: fetchedData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['admin', 'user-detail', userId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      
      const userResponse = await fetch(`${BASE_URL}/v1/user/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!userResponse.ok) throw new Error('Failed to fetch users');
      const allUsers = await userResponse.json();
      const foundUser = allUsers.find((u: UserDetail) => u.id === parseInt(userId || '0'));
      if (!foundUser) throw new Error('User not found');

      const [userGroups, userCollections, imageResponse] = await Promise.all([
        groupApi.getMyGroups(token).catch(() => []),
        collectionApi.getMyCollections(token).catch(() => []),
        fetch(`${BASE_URL}/v1/image/admin/user/${encodeURIComponent(foundUser.email)}/count`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
      ]);

      let imageCount = 0;
      if (imageResponse && imageResponse.ok) {
        const imageData = await imageResponse.json();
        imageCount = imageData.count || 0;
      }

      return {
        user: foundUser,
        groups: userGroups,
        collections: userCollections,
        stats: {
          imageCount,
          groupCount: userGroups.length,
          collectionCount: userCollections.length,
        },
      };
    },
    enabled: !!userId,
  });

  const user = fetchedData?.user || null;
  const groups = fetchedData?.groups || [];
  const collections = fetchedData?.collections || [];
  const stats = fetchedData?.stats || { imageCount: 0, groupCount: 0, collectionCount: 0 };
  const error = queryError ? (queryError as Error).message : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'User not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')} sx={{ mt: 2 }}>
          Back to Admin Dashboard
        </Button>
      </Box>
    );
  }

  const runPurge = async () => {
    if (!user) return;
    try {
      setPurgeRunning(true);
      setPurgeError(null);
      const token = await getAccessTokenSilently();
      const report = await adminApi.purgeUserData(
        user.id,
        { dryRun: purgeDryRun, confirm: purgeConfirmText || undefined },
        token
      );
      setPurgeReport(report);
      setPurgeDialogOpen(false);
      if (!purgeDryRun) {
        queryClient.invalidateQueries({ queryKey: ['admin'] });
      }
    } catch (err: any) {
      setPurgeError(err?.message || 'Failed to purge user data');
    } finally {
      setPurgeRunning(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        Back to Admin Dashboard
      </Button>

      {/* User Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: 32 }}>
            {user.name?.charAt(0) || user.email.charAt(0)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h4">{user.name || user.givenName || 'N/A'}</Typography>
              {user.isSuperAdmin && (
                <Chip icon={<AdminIcon />} label="Super Admin" color="error" size="small" />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              </Box>
              {user.affiliation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">{user.affiliation}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">ID: {user.id}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                <ImageIcon />
              </Avatar>
              <Box>
                <Typography variant="h4">{stats.imageCount}</Typography>
                <Typography color="text.secondary">Images</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                <GroupIcon />
              </Avatar>
              <Box>
                <Typography variant="h4">{stats.groupCount}</Typography>
                <Typography color="text.secondary">Groups</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                <FolderIcon />
              </Avatar>
              <Box>
                <Typography variant="h4">{stats.collectionCount}</Typography>
                <Typography color="text.secondary">Collections</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Groups List */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6">Groups ({groups.length})</Typography>
        </Box>
        {groups.length > 0 ? (
          <List>
            {groups.map((group, index) => (
              <React.Fragment key={group.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate(`/groups/${group.id}`)}
                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <ListItemText
                      primary={group.name}
                      secondary={group.description || 'No description'}
                    />
                    <Chip
                      label={group.isPublic ? 'Public' : 'Private'}
                      size="small"
                      color={group.isPublic ? 'success' : 'default'}
                    />
                  </ListItemButton>
                </ListItem>
                {index < groups.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No groups found</Typography>
          </Box>
        )}
      </Paper>

      {/* Collections List */}
      <Paper>
        <Box sx={{ p: 2, bgcolor: 'info.main', color: 'white' }}>
          <Typography variant="h6">Collections ({collections.length})</Typography>
        </Box>
        {collections.length > 0 ? (
          <List>
            {collections.map((collection, index) => (
              <React.Fragment key={collection.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => navigate(`/collections/${collection.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <ListItemText
                      primary={collection.name}
                      secondary={
                        <React.Fragment>
                          {collection.description || 'No description'}
                          {' • '}
                          {collection.imageCount} images
                        </React.Fragment>
                      }
                    />
                    {collection.isShared && (
                      <Chip label="Shared" size="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
                {index < collections.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No collections found</Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ mt: 3, border: '1px solid', borderColor: 'error.main' }}>
        <Box sx={{ p: 2, bgcolor: 'error.main', color: 'white' }}>
          <Typography variant="h6">Danger Zone</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will remove the user from all groups (without deleting groups), delete all images owned by the user
            (database records) and attempt to delete the associated stored files.
          </Typography>
          <Button variant="contained" color="error" onClick={() => setPurgeDialogOpen(true)}>
            Delete all user data
          </Button>
          {purgeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {purgeError}
            </Alert>
          )}
        </Box>
      </Paper>

      {purgeReport && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
            <Typography variant="h6">Deletion report</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(purgeReport, null, 2)}
            </pre>
          </Box>
        </Paper>
      )}

      <Dialog open={purgeDialogOpen} onClose={() => (purgeRunning ? null : setPurgeDialogOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Delete all user data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            User:
            {' '}
            {user.email}
            {' '}
            (ID: {user.id})
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={purgeDryRun}
                onChange={(e) => setPurgeDryRun(e.target.checked)}
                disabled={purgeRunning}
              />
            }
            label="Dry run (no deletions, just report)"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Type DELETE to confirm"
            value={purgeConfirmText}
            onChange={(e) => setPurgeConfirmText(e.target.value)}
            disabled={purgeRunning || purgeDryRun}
            helperText={purgeDryRun ? 'Dry run does not require confirmation.' : 'Required to run a real purge.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeDialogOpen(false)} disabled={purgeRunning}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={runPurge}
            disabled={purgeRunning || (!purgeDryRun && purgeConfirmText !== 'DELETE')}
          >
            {purgeRunning ? 'Running…' : purgeDryRun ? 'Run dry run' : 'Purge now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserDetail;
